import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { message } from 'antd';

import { messageApi } from '../utils/messageHolder';

// Dev local: VITE_API_BASE_URL vacio → axios pega a /asicontadores (path
// relativo) y Vite proxea al CloudFront publico.
// Prod: el build debe setear VITE_API_BASE_URL=https://api.a2csuite.com/asicontadores.
const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/asicontadores';

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Enviar/recibir cookies httpOnly con cada request.
  withCredentials: true,
});

// _retry marca una request que ya se reintentó una vez tras un refresh, para
// no volver a intentarlo si vuelve a dar 401 (evita loop infinito).
type RetriableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };

let isRefreshing = false;
let refreshSubscribers: ((refreshed: boolean) => void)[] = [];

function onRefreshed(callback: (refreshed: boolean) => void) {
  refreshSubscribers.push(callback);
}

// Notifica a todas las requests que quedaron encoladas esperando el refresh
// en curso, sea que haya salido bien o mal — si no se notifica también en la
// falla, esas requests quedan colgadas para siempre (nunca resuelven ni
// rechazan).
function notifySubscribers(refreshed: boolean) {
  refreshSubscribers.forEach(callback => callback(refreshed));
  refreshSubscribers = [];
}

function redirectToLogin() {
  // Evitar redirecciones múltiples en rápida sucesión.
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

async function refreshAccessToken(): Promise<boolean> {
  try {
    // apiClient (no axios suelto): el bypass /auth/* del interceptor de más
    // abajo corta esta request antes de la lógica de reintento, así que no
    // hay riesgo de recursión al llamar /auth/refresh a través del mismo
    // cliente que dispara el interceptor.
    await apiClient.post('/auth/refresh', {});
    return true;
  } catch {
    return false;
  }
}

// Interceptor de errores: muestra notificación al usuario para cualquier
// error HTTP. Las páginas pueden seguir manejando errores individualmente
// si necesitan lógica adicional (el reject se mantiene).
apiClient.interceptors.response.use(
  (res) => res,
  async (err: AxiosError<{ error?: string; message?: string }>) => {
    const originalRequest = err.config as RetriableRequestConfig | undefined;
    const status = err.response?.status;
    const data = err.response?.data;

    // Los endpoints /auth/* quedan FUERA de este interceptor: se rechazan tal
    // cual, igual que hacía el fetch nativo de src/auth/session.ts antes de la
    // migración. Sin toast global (las pantallas de auth muestran el error en
    // su propio <Alert> inline) y sin auto-refresh/redirect (AuthProvider
    // decide cuándo refrescar). Regla por PREFIJO: cualquier endpoint /auth/*
    // futuro queda excluido automáticamente — decisión consciente y documentada.
    if (originalRequest?.url?.startsWith('/auth/')) {
      return Promise.reject(err);
    }

    // Si es 401 y no estamos ya en /auth/refresh, intentamos renovar la sesión.
    // (La condición /auth/refresh es redundante hoy: el bypass /auth/* de
    // arriba ya cortó antes. Se conserva como defensa en profundidad por si
    // ese bypass se acota en el futuro — evita disparar un refresh a partir
    // del 401 del propio refresh.)
    if (status === 401 && originalRequest && !originalRequest.url?.includes('/auth/refresh')) {
      // Ya reintentamos esta request una vez tras un refresh y sigue en 401
      // (sesión realmente inválida) — no reintentar de nuevo, iría a loop.
      if (originalRequest._retry) {
        redirectToLogin();
        return Promise.reject(err);
      }

      if (!isRefreshing) {
        isRefreshing = true;
        const refreshed = await refreshAccessToken();
        isRefreshing = false;
        notifySubscribers(refreshed);

        if (refreshed) {
          originalRequest._retry = true;
          return apiClient(originalRequest);
        }

        // Refresh falló: redirigir a login sin mostrar toast.
        redirectToLogin();
        return Promise.reject(err);
      }

      // Si ya estamos refrescando, encolamos la request para reintentarla —
      // o rechazarla, si el refresh en curso termina fallando.
      return new Promise((resolve, reject) => {
        onRefreshed((refreshed) => {
          if (refreshed) {
            originalRequest._retry = true;
            resolve(apiClient(originalRequest));
          } else {
            reject(err);
          }
        });
      });
    }

    // No mostrar popup para 401 (se maneja con redirect a login)
    if (status === 401) {
      return Promise.reject(err);
    }

    // Extraer mensaje legible del backend o usar fallback
    const msg =
      data?.error ||
      data?.message ||
      (status === 403 ? 'No tienes permisos para esta acción' : null) ||
      (status === 404 ? 'Recurso no encontrado' : null) ||
      (status === 500 ? 'Error interno del servidor' : null) ||
      (status ? `Error ${status}` : 'Error de conexión — verifica tu red');

    (messageApi ?? message).error(msg);
    return Promise.reject(err);
  },
);
